import { useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/lib/auth";
import {
  Search,
  Rocket,
  LayoutDashboard,
  ClipboardCheck,
  PlayCircle,
  ShieldAlert,
  Thermometer,
  AlertTriangle,
  Boxes,
  Wrench,
  GraduationCap,
  Zap,
  Clock,
  Trophy,
  FileBarChart,
  Users,
  Building2,
  Bot,
  CreditCard,
  Smartphone,
} from "lucide-react";

const MANAGE = ["super_admin", "company_admin", "manager", "supervisor"];
const ALL = ["super_admin", "company_admin", "manager", "supervisor", "operator", "auditor"];

const TOPICS = [
  {
    id: "primeiros-passos",
    icon: Rocket,
    title: "Primeiros passos",
    roles: ALL,
    intro: "O caminho recomendado para colocar sua operação no ar.",
    steps: [
      "Cadastre suas Unidades (menu Unidades) — cada cozinha/loja é uma unidade.",
      "Cadastre os Setores (ex.: Cozinha, Confeitaria) e depois os Usuários, definindo perfil, unidade e setor de cada um.",
      "Crie seus Checklists (menu Checklists → Novo checklist): etapa 1 configura nome, frequência, horário e visibilidade; etapa 2 define as perguntas (ou gere com IA).",
      "Cadastre os itens de Estoque com estoque mínimo e lotes com validade.",
      "Ative o Controle de Ponto criando um usuário Totem e cadastrando a foto do rosto de cada colaborador.",
      "Acompanhe tudo pelo Dashboard e pelos Relatórios.",
    ],
  },
  {
    id: "dashboard",
    icon: LayoutDashboard,
    title: "Dashboard & Meu Dia",
    roles: ALL,
    intro: "Visão geral da operação para gestores e lista do dia para a equipe.",
    steps: [
      "Gestores veem KPIs, gráficos de Análise, ranking de unidades e análise preditiva por IA.",
      "Operadores veem o Meu Dia: checklists pendentes com horário, agrupados por atribuição.",
      "Clique em um checklist pendente para abrir o popup de execução rápida — responda e finalize sem sair do dashboard.",
      "O sino no topo mostra pendências, não conformidades, solicitações de ponto e advertências a assinar.",
    ],
  },
  {
    id: "checklists",
    icon: ClipboardCheck,
    title: "Checklists",
    roles: MANAGE,
    intro: "Modelos de verificação que geram execuções automáticas para a equipe.",
    steps: [
      "Clique em Novo checklist. Etapa 1 — Configurações: nome, categoria, frequência (diária/semanal/mensal/turno), horário de entrada nas pendências, prazo em minutos, unidades e visibilidade (todos, perfis, setores ou pessoas).",
      "Avance para a Etapa 2 — Perguntas: adicione perguntas tipadas (texto, número, temperatura com limites, sim/não, foto com validação IA, múltipla escolha, assinatura, QR code, controle de estoque). Você pode voltar à etapa 1 sem perder nada.",
      "Use Gerar com IA para criar as perguntas automaticamente descrevendo sua operação.",
      "Perguntas de temperatura fora dos limites e respostas não conformes podem gerar Ocorrências automaticamente.",
      "O agendamento é criado sozinho: no horário definido, o checklist entra nas pendências dos responsáveis.",
    ],
  },
  {
    id: "execucoes",
    icon: PlayCircle,
    title: "Execuções",
    roles: ALL,
    intro: "Histórico e andamento de cada checklist executado.",
    steps: [
      "Acompanhe execuções pendentes, em andamento e concluídas (com paginação).",
      "Clique em Ver/Continuar para abrir a execução completa.",
      "Perguntas de foto podem exigir validação por IA antes do envio.",
      "Sem internet? O checklist é salvo no dispositivo e enviado automaticamente quando a conexão voltar.",
    ],
  },
  {
    id: "auditorias",
    icon: ShieldAlert,
    title: "Auditorias",
    roles: [...MANAGE, "auditor"],
    intro: "Avaliações pontuadas com resultado oficial.",
    steps: [
      "Crie um template de auditoria com seções e perguntas com peso.",
      "Execute a auditoria na unidade: cada resposta soma pontos conforme o peso.",
      "O resultado sai automático: Aprovado, Aprovado com ressalvas ou Reprovado (conforme a nota de corte).",
      "Exporte o resultado nos Relatórios.",
    ],
  },
  {
    id: "appcc",
    icon: Thermometer,
    title: "APPCC",
    roles: MANAGE,
    intro: "Pontos Críticos de Controle com limites e monitoramento.",
    steps: [
      "Cadastre cada PCC (ex.: Câmara fria 1) com limites mínimo e máximo.",
      "Registre monitoramentos manualmente ou via checklists de temperatura.",
      "Medições fora do limite geram desvio e aparecem no dashboard e na análise preditiva.",
    ],
  },
  {
    id: "ocorrencias",
    icon: AlertTriangle,
    title: "Ocorrências",
    roles: ALL,
    intro: "Não conformidades da operação, manuais ou automáticas.",
    steps: [
      "Registre uma ocorrência manualmente com título, descrição, prioridade e fotos.",
      "Checklists com respostas não conformes podem abrir ocorrências sozinhos.",
      "Trate cada ocorrência mudando o status até Resolvida.",
    ],
  },
  {
    id: "estoque",
    icon: Boxes,
    title: "Estoque (PVPS e etiquetas QR)",
    roles: ALL,
    intro: "Controle de saldo com validade por lote e regra PVPS.",
    steps: [
      "Cadastre itens com unidade de medida e estoque mínimo; adicione lotes com data de validade e peso unitário.",
      "PVPS (Primeiro que Vence, Primeiro que Sai): toda baixa desconta automaticamente dos lotes que vencem primeiro.",
      "Dentro do produto, exporte a etiqueta em PDF (50x30mm) com QR code para impressora térmica.",
      "Para consumir, use Consumo por QR: escaneie a etiqueta e informe a quantidade — a baixa respeita o PVPS.",
      "Itens abaixo do estoque mínimo aparecem nos alertas e no relatório Sugestão de Compras.",
      "Consumos capturados via checklist passam por aprovação em Consumos antes de baixar o saldo.",
    ],
  },
  {
    id: "equipamentos",
    icon: Wrench,
    title: "Equipamentos",
    roles: MANAGE,
    intro: "Inventário com manutenções e alertas.",
    steps: [
      "Cadastre equipamentos por tipo e unidade.",
      "Registre manutenções preventivas/corretivas com datas.",
      "Acompanhe vencimentos de manutenção nos alertas.",
    ],
  },
  {
    id: "treinamentos",
    icon: GraduationCap,
    title: "Treinamentos & Certificados",
    roles: ALL,
    intro: "Capacitação com quiz, ciência e certificado em PDF.",
    steps: [
      "Gestores criam treinamentos com conteúdo, fotos/vídeos e quiz (ou geram com IA).",
      "O colaborador lê o conteúdo, responde o quiz e registra a ciência.",
      "Após concluir, o botão Certificado baixa o Certificado de Treinamento em PDF.",
      "Gestores acompanham quem concluiu em cada treinamento.",
    ],
  },
  {
    id: "regras",
    icon: Zap,
    title: "Regras automáticas",
    roles: MANAGE,
    intro: "Automações do tipo 'se acontecer X, faça Y'.",
    steps: [
      "Crie uma regra com condições (ex.: temperatura acima do limite) e ações (ex.: abrir ocorrência, notificar).",
      "As regras rodam automaticamente a cada execução de checklist.",
    ],
  },
  {
    id: "ponto",
    icon: Clock,
    title: "Controle de Ponto",
    roles: ALL,
    intro: "Totem com reconhecimento facial, espelho mensal e advertências.",
    steps: [
      "Gestor: crie um usuário com perfil Totem e deixe-o logado no tablet da cozinha; cadastre a foto do rosto de cada colaborador em Usuários.",
      "Colaborador: bata o ponto no totem (4 batidas/dia) — o reconhecimento é feito por IA e o resultado é falado em voz alta.",
      "Espelho de ponto: cada colaborador confere suas batidas do mês, solicita ajustes, justifica ausências (com atestado analisado por IA) e assina digitalmente o espelho.",
      "Gestão de Ponto (gestores): aprova ajustes, edita batidas, acompanha banco de horas e gera advertências por IA para atrasos/faltas.",
      "Advertências chegam no sino do colaborador para leitura e assinatura digital; o PDF fica arquivado.",
    ],
  },
  {
    id: "ranking",
    icon: Trophy,
    title: "Ranking da Equipe (Gamificação)",
    roles: ALL,
    intro: "Competição mensal saudável por % de aproveitamento.",
    steps: [
      "A pontuação do mês considera o percentual de conclusão e conformidade dos checklists de cada pessoa — quem tem menos checklists não sai prejudicado.",
      "Somente operadores e auditores competem (perfis de gestão ficam de fora).",
      "Gestores definem o Prêmio do Mês; o campeão entra no histórico de vencedores e pode ser notificado.",
    ],
  },
  {
    id: "relatorios",
    icon: FileBarChart,
    title: "Relatórios",
    roles: MANAGE,
    intro: "Exportações em PDF, Excel e CSV — e envio por WhatsApp.",
    steps: [
      "Disponíveis: Conformidade, Pontualidade, Ocorrências, Auditorias, Estoque e Sugestão de Compras (consumo de 30 dias × estoque mínimo).",
      "Clique no formato desejado (PDF/XLSX/CSV) para baixar.",
      "Use o botão WhatsApp do relatório para enviá-lo ao número configurado.",
    ],
  },
  {
    id: "usuarios",
    icon: Users,
    title: "Usuários & Perfis",
    roles: MANAGE,
    intro: "Equipe com papéis e permissões distintas.",
    steps: [
      "Perfis: Admin (tudo), Gestor, Supervisor, Operador (executa), Auditor (audita) e Totem (ponto).",
      "Defina unidade, setor e foto do rosto (para o ponto) de cada usuário.",
      "A visibilidade dos checklists pode ser por perfil, setor ou pessoa específica.",
    ],
  },
  {
    id: "unidades",
    icon: Building2,
    title: "Unidades & Setores",
    roles: MANAGE,
    intro: "A estrutura física e organizacional da empresa.",
    steps: [
      "Cadastre cada unidade (cozinha, loja, filial) — checklists e estoque são por unidade.",
      "Crie setores para organizar a equipe e direcionar checklists.",
      "Com várias unidades, o Dashboard Corporativo compara o desempenho entre elas.",
    ],
  },
  {
    id: "chatbot",
    icon: Bot,
    title: "Chatbot WhatsApp",
    roles: MANAGE,
    intro: "Operadores interagem com o sistema pelo WhatsApp.",
    steps: [
      "Comandos: 'status' lista pendências; 'ocorrencia: descrição' abre uma ocorrência; 'ajuda' mostra o menu.",
      "Mensagens informais (ex.: 'temperatura camara 1 5 graus') são interpretadas por IA.",
      "Requer a integração Twilio configurada em Integrações.",
    ],
  },
  {
    id: "planos",
    icon: CreditCard,
    title: "Planos & Limites",
    roles: ["super_admin", "company_admin"],
    intro: "Assinatura, limites e upgrade.",
    steps: [
      "Cada plano tem limites de unidades, usuários e checklists — o uso atual aparece em Planos & Cobrança.",
      "Ao atingir um limite, o botão de criação é bloqueado com aviso para upgrade.",
      "Faça upgrade/downgrade a qualquer momento; o pagamento é processado com segurança via Stripe.",
    ],
  },
  {
    id: "pwa",
    icon: Smartphone,
    title: "Instalar no celular (PWA)",
    roles: ALL,
    intro: "Use o Ultra como aplicativo, inclusive offline.",
    steps: [
      "No celular, abra o sistema no navegador e toque em 'Adicionar à tela inicial' (ou aceite o aviso de instalação).",
      "O app abre em tela cheia com o ícone do Ultra.",
      "Checklists respondidos sem internet são enviados automaticamente quando a conexão voltar.",
    ],
  },
];

export default function Help() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");

  const topics = useMemo(() => {
    const role = user?.role || "operator";
    const visible = role === "super_admin" ? TOPICS : TOPICS.filter((t) => t.roles.includes(role));
    const q = query.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.intro.toLowerCase().includes(q) ||
        t.steps.some((s) => s.toLowerCase().includes(q))
    );
  }, [user, query]);

  return (
    <Layout
      title="Me ajude"
      subtitle="Instruções do sistema e primeiros passos de cada menu"
    >
      <div className="max-w-3xl space-y-5" data-testid="help-page">
        <Card className="p-4 rounded-xl border-stone-200 bg-white">
          <div className="relative">
            <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar instrução... ex: etiqueta, ponto, certificado"
              className="pl-9 bg-white"
              data-testid="help-search"
            />
          </div>
          <p className="text-[11px] text-stone-500 font-body mt-2">
            Não achou o que procurava? Fale com nosso assistente no menu{" "}
            <span className="font-semibold">Suporte</span> — a IA responde na hora.
          </p>
        </Card>

        {topics.length === 0 && (
          <Card className="p-8 text-center text-stone-500 font-body rounded-xl border-stone-200 bg-white">
            Nenhuma instrução encontrada para "{query}".
          </Card>
        )}

        <Accordion type="multiple" className="space-y-3" data-testid="help-topics">
          {topics.map((t) => {
            const Icon = t.icon;
            return (
              <AccordionItem
                key={t.id}
                value={t.id}
                className="border border-stone-200 rounded-xl bg-white px-4 data-[state=open]:shadow-sm"
                data-testid={`help-topic-${t.id}`}
              >
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3 text-left">
                    <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-sm">{t.title}</div>
                      <div className="text-xs text-stone-500 font-body font-normal">{t.intro}</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <ol className="space-y-2 ml-1">
                    {t.steps.map((s, i) => (
                      <li key={s} className="flex items-start gap-2.5 text-sm font-body text-stone-700">
                        <Badge
                          variant="outline"
                          className="mt-0.5 h-5 w-5 p-0 justify-center rounded-full border-emerald-200 text-emerald-800 text-[10px] shrink-0"
                        >
                          {i + 1}
                        </Badge>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </Layout>
  );
}
